import { useEffect, useMemo, useState } from 'react';

import { Button, Center, CloseButton, Flex, Text } from '@chakra-ui/react';
import { useETHSafeMode } from '@stader-labs/web-sdk';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
// Hooks
import { useCookies } from 'react-cookie';

// Constants
import { useDappSDKProvider } from '@/dapp-sdk-v2';
import { useEmbed } from '@/dapp-sdk-v2/hooks/useEmbed';
import { useCMSData } from '@/providers/CMSDataProvider';
import { getCurrentChain } from '@/utils/common';

const initialState: any = {
  text: null,
  link: null,
  show: false,
  ctaText: null,
  isClosable: true,
  type: '',
  htmlEntity: '',
};

const VID = 'vid';

const TOKEN = process.env.NEXT_PUBLIC_TOKEN;

const Banner = () => {
  const router = useRouter();
  const pathname = usePathname();
  const token = getCurrentChain(pathname);
  const cookieName = `promo_banner_disabled_${token}`;
  const { isEmbed } = useEmbed();

  const { data: isSafeMode } = useETHSafeMode({
    options: {
      staleTime: 60 * 60 * 1000,
    },
  });
  // Todo - remove this temp fix.
  const { setUserData } = useDappSDKProvider();
  const [cookies, setCookie] = useCookies([cookieName]);
  const [bannerDetails, setBannerDetails] = useState(initialState);

  const cmsData = useCMSData((data) => data.stakeGeneral);

  const isBannerExist = useMemo(() => {
    return (
      cmsData &&
      cmsData?.banner &&
      (cmsData.banner?.text ||
        cmsData.banner?.textSafeMode ||
        (isEmbed && cmsData.banner_ledger?.text))
    );
  }, [isEmbed, cmsData]);

  useEffect(() => {
    if (router.query[VID] && !cookies[VID]) {
      setCookie(VID, router.query[VID]);
    }
  }, [router.query, cookies, setCookie]);

  useEffect(() => {
    if (isSafeMode && isBannerExist) {
      const bannerData = cmsData?.banner;
      setBannerDetails({
        ...bannerData,
        text: bannerData?.textSafeMode || bannerData?.text,
      });
    }
  }, [isBannerExist, isSafeMode]);

  useEffect(() => {
    if (cookies[cookieName]) {
      return;
    }
    if (token && !isSafeMode && isBannerExist) {
      if (isEmbed && cmsData?.banner_ledger?.text) {
        setBannerDetails(cmsData?.banner_ledger);
      } else {
        setBannerDetails(cmsData?.banner);
      }
    }
  }, [
    cookies,
    isSafeMode,
    isBannerExist,
    isEmbed,
    cookieName,
    token,
    cmsData?.banner_ledger,
    cmsData?.banner,
  ]);

  const closeBanner = () => {
    setCookie(cookieName, true, {
      maxAge: 1209600,
    });
    setBannerDetails((prev: any) => {
      return prev ? { ...prev, show: false } : { show: false };
    });
  };

  const getBGForBanner = useMemo(() => {
    if (isSafeMode) {
      return '#FF4D4D';
    } else if (bannerDetails.type === 'info') {
      return '#2196F3';
    } else if (bannerDetails.type === 'warning') {
      return '#FBC828';
    }
    return 'primary';
  }, [isSafeMode, bannerDetails]);

  useEffect(() => {
    if (bannerDetails.show && bannerDetails.text) {
      setUserData({ isBannerVisible: true });
    } else {
      setUserData({ isBannerVisible: false });
    }
  }, [bannerDetails]);


  if (bannerDetails.show && bannerDetails.text) {
    return (
      <Flex
        w="100%"
        bg={getBGForBanner}
        color={TOKEN === 'BNB' ? "#142528" : "#FFFFFF"}
        _dark={{
          color: '#142528',
        }}
        fontWeight="600"
        alignItems="center"
        pos={{ base: 'relative', lg: 'fixed' }}
        top="0"
        height={{ base: "72px", lg: "48px" }}
        zIndex={4}
      >
        <Center flexGrow={1} flexDir={{ base: 'column', lg: 'row' }} p="8px">
          <Text
            textAlign={{ base: 'left', md: 'center' }}
            fontSize={{ base: '12px', lg: '14px' }}
            lineHeight={{ base: '18px', lg: '20px' }}
          >
            <Text as="span">{bannerDetails.text}</Text>
            {bannerDetails.htmlEntity && !isSafeMode && (
              <Text
                display="inline"
                ml="2px"
                sx={{
                  '& p': {
                    display: 'inline',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: `<p>${bannerDetails.htmlEntity} </p>`,
                }}
              />
            )}
          </Text>
          {bannerDetails.link && bannerDetails.ctaText && (
            <>
              {
                TOKEN === 'BNB' ? (
                  <Button
                    as="a"
                    href={bannerDetails.link}
                    target="_blank"
                    rel="noreferrer"
                    ml={{ base: 0, lg: '12px' }}
                    mt={{ base: '8px', lg: 0 }}
                    bg="white"
                    color="#142528"
                    fontSize={{ base: '12px', lg: '14px' }}
                    fontWeight="600"
                    px="16px"
                    py="4px"
                    h="32px"
                    borderRadius="6px"
                    _hover={{
                      bg: 'rgba(255, 255, 255, 0.9)',
                      // transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    }}
                    _active={{
                      bg: 'rgba(255, 255, 255, 0.8)',
                      transform: 'translateY(0)',
                    }}
                    transition="all 0.2s"
                    cursor="pointer"
                  >
                    {bannerDetails.ctaText}
                  </Button>
                ) : (
                  <Text
                    as="a"
                    href={bannerDetails.link}
                    target="_blank"
                    rel="noreferrer"
                    p="0.25rem"
                    fontSize={{ base: '12px', lg: '14px' }}
                    lineHeight={{ base: '18px', lg: '20px' }}
                    color="white"
                    textDecor="underline !important"
                    cursor="pointer"
                  >
                    {bannerDetails.ctaText}
                  </Text>
                )
              }

            </>
          )}
        </Center>

        {bannerDetails.isClosable && (
          <CloseButton
            onClick={() => {
              closeBanner();
            }}
          />
        )}
      </Flex>
    );
  }
  return null;
};

export default Banner;
